﻿import React from 'react';
import { Check, ChevronDown, ExternalLink, Mail, Search } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { channelApi } from '../config/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const field = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-400';
const label = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500';

function formatDateTimeWib(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return `${new Intl.DateTimeFormat('id-ID', {
    timeZone: 'Asia/Jakarta',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d).replace(/\./g, ':')} WIB`;
}

function apiErrorMessage(e, fallback = 'Terjadi kesalahan') {
  const data = e?.response?.data;
  if (data && typeof data === 'object' && data.message) return data.message;
  if (typeof data === 'string' && data.trim()) {
    const text = data.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (/bad gateway|502/i.test(text) || !text) {
      return 'Backend tidak merespons (502). Pastikan API Integrated di port 17011 berjalan, lalu refresh halaman.';
    }
    return text.slice(0, 280);
  }
  if (e?.code === 'ECONNABORTED') return 'Request timeout. Coba lagi — pengiriman email membutuhkan waktu lebih lama.';
  if (e?.message && /status code 502/i.test(e.message)) {
    return 'Backend tidak merespons (502). Pastikan API Integrated di port 17011 berjalan, lalu refresh halaman.';
  }
  return e?.message || fallback;
}

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function memberLabelFromCode(code) {
  const key = String(code || '').trim().toLowerCase();
  const map = {
    inj: 'InJourney Group',
    injourney: 'InJourney Group',
    internal: 'Internal PRIME',
    ias: 'IAS / Airports',
    airport: 'IAS / Airports',
    airports: 'IAS / Airports',
    aviation: 'Aviation Services',
    aviasi: 'Aviation Services',
    hospitality: 'Hospitality',
    hin: 'Hospitality',
    idm: 'Destination Management',
    twc: 'Destination Management / TWC',
    tmii: 'Destination Management / TMII',
    retail: 'Retail',
    sarinah: 'Retail / Sarinah',
    srn: 'Retail / Sarinah',
    itdc: 'Tourism Development / ITDC',
    tourism: 'Tourism Development',
  };

  if (!key) return 'Unassigned Member';
  return map[key] || key.toUpperCase();
}

function itemKey(item) {
  return [
    item.type || 'catalog',
    item.packageCode || item.productCode || item.code || '',
    item.source || 'source',
    item.packageId || item.productId || '',
  ].join('::');
}

function itemCode(item) {
  return item.packageCode || item.productCode || item.code || '';
}

function itemName(item) {
  return item.packageName || item.productName || item.name || itemCode(item) || '-';
}

function addCalendarDays(value, days) {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return '';
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function itemHasHotel(item) {
  return Boolean(item?.hasHotel || item?.isHotel || item?.containsHotel);
}

function stayDatePayload(item, form) {
  if (!itemHasHotel(item)) return {};
  return {
    check_in_date: form.travelDate,
    check_out_date: item?.type === 'package'
      ? addCalendarDays(form.travelDate, 1)
      : form.checkOutDate,
  };
}

const hinHotelEmailTemplates = [
  {
    key: 'customer-waiting',
    title: 'Customer - Menunggu Konfirmasi Hotel',
    description: 'Dikirim bersamaan dengan konfirmasi ke hotel. Reservasi belum dikonfirmasi dan customer diminta menunggu keputusan hotel.',
  },
  {
    key: 'hotel-paid',
    title: 'Hotel - Pembayaran Diterima',
    description: 'Email ke hotel hanya setelah pembayaran booking terverifikasi PAID dan kamar dikunci untuk tamu.',
  },
  {
    key: 'hotel-confirmation',
    title: 'Konfirmasi ke Hotel',
    description: 'Email ke HIN/hotel dengan tombol Kamar Tersedia dan Kamar Full.',
  },
  {
    key: 'customer-available',
    title: 'Customer - Kamar Tersedia',
    description: 'Email ke customer untuk lanjut pembayaran melalui Indonesia Journey.',
  },
  {
    key: 'customer-paid',
    title: 'Customer - Pemesanan Berhasil',
    description: 'Email konfirmasi pembayaran dan reservasi hotel.',
  },
  {
    key: 'customer-expired',
    title: 'Customer - Pembayaran Kedaluwarsa',
    description: 'Email pembatalan setelah batas pembayaran berakhir.',
  },
  {
    key: 'customer-unavailable',
    title: 'Customer - Kamar Tidak Tersedia',
    description: 'Email ke customer saat hotel mengonfirmasi kamar penuh.',
  },
];

function itemBadge(item) {
  return item.type === 'package' ? 'Package' : 'Single Product';
}

function itemSearchText(item) {
  return [
    itemName(item),
    itemCode(item),
    item.memberCode,
    item.member?.code,
    memberLabelFromCode(item.memberCode),
    item.memberName,
    item.member?.name,
    item.source,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}


function CatalogCombobox({ groups, value, onChange, placeholder, loading, typeLabel }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const allItems = React.useMemo(() => groups.flatMap((group) => group.items), [groups]);
  const selected = allItems.find((item) => itemKey(item) === value) || null;
  const needle = query.trim().toLowerCase();

  const visibleGroups = React.useMemo(() => {
    if (!needle) return groups;
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => itemSearchText(item).includes(needle)),
      }))
      .filter((group) => group.items.length > 0);
  }, [groups, needle]);

  React.useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  return (
    <div>
      <label className={label}>Pilih dari Catalog PRIME</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`${field} flex min-h-10 items-center justify-between gap-3 text-left`}
            aria-expanded={open}
          >
            <span className={`min-w-0 flex-1 truncate ${selected ? 'text-slate-900' : 'text-slate-500'}`}>
              {selected ? `${itemName(selected)} [${itemCode(selected)}]` : placeholder}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] min-w-[320px] max-w-[680px] p-0"
        >
          <div className="border-b border-slate-200 p-2.5">
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3">
              <Search className="h-4 w-4 shrink-0 text-slate-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cari nama product, code, atau member..."
                className="h-10 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setOpen(false);
                }}
              />
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto p-1.5">
            {loading ? (
              <div className="px-3 py-6 text-center text-sm text-slate-500">Memuat catalog...</div>
            ) : visibleGroups.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-slate-500">Catalog tidak ditemukan.</div>
            ) : (
              visibleGroups.map((group) => (
                <div key={group.key} className="mb-2 last:mb-0">
                  <div className="sticky top-0 z-10 bg-slate-50 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    {group.label} ({group.items.length})
                  </div>
                  <div className="py-1">
                    {group.items.map((item) => {
                      const key = itemKey(item);
                      const active = key === value;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            onChange(key);
                            setOpen(false);
                          }}
                          className={`flex w-full items-start gap-2 rounded-md px-3 py-2 text-left text-sm transition hover:bg-slate-100 ${active ? 'bg-slate-100 font-semibold text-slate-900' : 'text-slate-700'}`}
                        >
                          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                            {active ? <Check className="h-4 w-4" /> : null}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate">{itemName(item)}</span>
                            <span className="mt-0.5 block truncate text-xs font-normal text-slate-500">
                              {itemCode(item)} · {item.memberName || memberLabelFromCode(item.memberCode)}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="border-t border-slate-200 px-3 py-2 text-xs text-slate-500">
            {allItems.length} item {typeLabel} · Search tersedia di dalam select
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default function PurchaseSimulator() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [catalog, setCatalog] = React.useState([]);
  const [channels, setChannels] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [initialLoading, setInitialLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');
  const [emailNotice, setEmailNotice] = React.useState('');
  const [selectedCatalogKey, setSelectedCatalogKey] = React.useState('');
  const [memberInvoiceOptions, setMemberInvoiceOptions] = React.useState([]);
  const [memberInvoiceCode, setMemberInvoiceCode] = React.useState('');
  const [memberInvoiceAudit, setMemberInvoiceAudit] = React.useState(null);
  const [form, setForm] = React.useState({
    kind: 'package',
    code: '',
    displayName: '',
    memberCode: '',
    travelDate: new Date().toISOString().slice(0, 10),
    checkOutDate: addCalendarDays(new Date().toISOString().slice(0, 10), 1),
    name: 'Demo Customer',
    email: '',
    phone: '',
    hotelEmail: '',
    channelClientId: '',
    dryRun: true,
    availabilitySimulation: 'AUTO',
  });

  const load = React.useCallback(async () => {
    setInitialLoading(true);
    setError('');

    const [catalogResult, channelsResult] = await Promise.allSettled([
      channelApi.get('/admin/simulator/catalog?limit=300'),
      channelApi.get('/admin/simulator/channels'),
    ]);

    if (catalogResult.status === 'fulfilled') {
      setCatalog(Array.isArray(catalogResult.value.data?.data) ? catalogResult.value.data.data : []);
    } else {
      setCatalog([]);
      const e = catalogResult.reason;
      setError(e.response?.data?.message || e.message || 'Gagal membaca katalog PRIME lokal.');
    }

    if (channelsResult.status === 'fulfilled') {
      setChannels(Array.isArray(channelsResult.value.data?.data) ? channelsResult.value.data.data : []);
    } else {
      setChannels([]);
      console.warn('PRIME simulator channels unavailable:', channelsResult.reason);
    }

    setInitialLoading(false);
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const filteredCatalog = React.useMemo(
    () => {
      const memberFilter = String(searchParams.get('member') || '').trim().toLowerCase();
      return catalog.filter((item) => {
        const sameType = (item.type || 'product') === form.kind;
        if (!sameType) return false;
        if (!memberFilter) return true;
        const code = String(item.member?.code || item.memberCode || '').trim().toLowerCase();
        return code === memberFilter;
      });
    },
    [catalog, form.kind, searchParams],
  );

  const groupedCatalog = React.useMemo(() => {
    const groups = new Map();
    filteredCatalog.forEach((item) => {
      const memberKey = String(item.memberCode || '').trim().toLowerCase() || 'unassigned';
      const memberLabel = item.memberName || memberLabelFromCode(item.memberCode);
      if (!groups.has(memberKey)) {
        groups.set(memberKey, {
          key: memberKey,
          label: memberLabel,
          items: [],
        });
      }
      groups.get(memberKey).items.push(item);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        items: group.items.sort((a, b) => itemName(a).localeCompare(itemName(b))),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredCatalog]);

  React.useEffect(() => {
    if (!selectedCatalogKey) return;
    const exists = filteredCatalog.some((item) => itemKey(item) === selectedCatalogKey);
    if (!exists) {
      setSelectedCatalogKey('');
      setForm((prev) => ({
        ...prev,
        code: '',
        displayName: '',
        memberCode: '',
      }));
    }
  }, [filteredCatalog, selectedCatalogKey]);

  const chooseCatalog = (event) => {
    const value = event.target.value;
    setSelectedCatalogKey(value);
    setError('');
    setEmailNotice('');
    setMemberInvoiceOptions([]);
    setMemberInvoiceCode('');
    setMemberInvoiceAudit(null);
    setResult(null);

    if (!value) {
      setForm((prev) => ({
        ...prev,
        code: '',
        displayName: '',
        memberCode: '',
      }));
      return;
    }

    const item = filteredCatalog.find((entry) => itemKey(entry) === value);
    if (!item) return;

    setForm((prev) => ({
      ...prev,
      kind: item.type === 'product' ? 'product' : 'package',
      code: itemCode(item),
      displayName: itemName(item),
      memberCode: item.member?.code || item.memberCode || '',
      checkOutDate: item.type === 'package' && itemHasHotel(item)
        ? addCalendarDays(prev.travelDate, 1)
        : prev.checkOutDate,
    }));
  };

  const execute = async () => {
    setLoading(true);
    setError('');
    setEmailNotice('');
    setResult(null);

    try {
      const payload = {
        travel_date: form.travelDate,
        ...stayDatePayload(selectedCatalogItem, form),
        passengers: [{ name: form.name, gender: 'Laki-laki' }],
        contact_details: { email: form.email, phone: form.phone },
        dry_run: form.dryRun,
        channel_client_id: form.channelClientId || undefined,
        display_name: form.displayName,
        member_code: form.memberCode || undefined,
        availability_simulation: form.availabilitySimulation,
      };

      payload[form.kind === 'product' ? 'product_code' : 'package_code'] = form.code;
      const response = await channelApi.post('/admin/simulator/purchase', payload, { timeout: 60000 });
      setResult(response.data?.data || response.data);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const sendDummyEmail = async () => {
    if (!form.email) {
      setError('Isi Customer Email terlebih dahulu.');
      return;
    }
    if (!form.code) {
      setError('Pilih produk atau package dari katalog sebelum mengirim dummy e-ticket.');
      return;
    }

    setLoading(true);
    setError('');
    setEmailNotice('');

    try {
      const payload = {
        email: form.email,
        name: form.name,
        travel_date: form.travelDate,
        ...stayDatePayload(selectedCatalogItem, form),
        display_name: form.displayName,
        member_code: form.memberCode || undefined,
        type: form.kind === 'product' ? 'product' : 'package',
        contact_details: { email: form.email, phone: form.phone },
      };
      payload[form.kind === 'product' ? 'product_code' : 'package_code'] = form.code;

      // PDF + SMTP often needs >10s; default channelApi timeout caused proxy 502.
      const response = await channelApi.post('/admin/simulator/test-email', payload, { timeout: 60000 });

      setEmailNotice(response.data?.message || `Dummy e-ticket berhasil dikirim ke ${form.email}`);
    } catch (e) {
      setError('Gagal mengirim dummy e-ticket: ' + apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const buildInvoicePayload = () => {
    const payload = {
      email: form.email,
      name: form.name,
      travel_date: form.travelDate,
      ...stayDatePayload(selectedCatalogItem, form),
      display_name: form.displayName,
      member_code: form.memberCode || undefined,
      type: form.kind === 'product' ? 'product' : 'package',
      agent_name: form.companyName || selectedCatalogItem?.member?.name || memberLabelFromCode(form.memberCode) || 'Demo Agent',
      agentName: form.companyName || selectedCatalogItem?.member?.name || memberLabelFromCode(form.memberCode) || 'Demo Agent',
      contact_details: { email: form.email, phone: form.phone },
    };
    payload[form.kind === 'product' ? 'product_code' : 'package_code'] = form.code;
    return payload;
  };

  const loadMemberInvoiceOptions = async () => {
    if (!form.code) {
      setError('Pilih produk single atau package terlebih dahulu untuk melihat tujuan invoice member.');
      return;
    }
    setLoading(true);
    setError('');
    setMemberInvoiceAudit(null);
    try {
      const response = await channelApi.post('/admin/simulator/member-invoice/options', buildInvoicePayload(), { timeout: 60000 });
      const options = Array.isArray(response.data?.data?.members) ? response.data.data.members : [];
      setMemberInvoiceOptions(options);
      const preferred = form.kind === 'product' && form.memberCode
        ? options.find((item) => String(item.memberCode).toLowerCase() === String(form.memberCode).toLowerCase())
        : null;
      setMemberInvoiceCode((current) => {
        if (options.some((item) => item.memberCode === current)) return current;
        return preferred?.memberCode || options[0]?.memberCode || '';
      });
      setEmailNotice(
        options.length
          ? `${options.length} tujuan invoice member ditemukan (${form.kind === 'package' ? 'harga hanya produk member tujuan' : 'produk single'}).`
          : 'Belum ada member tujuan invoice (komponen tanpa MEMBER_CODE).',
      );
    } catch (e) {
      setError('Gagal membaca tujuan invoice member: ' + apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const previewMemberInvoice = async () => {
    if (!memberInvoiceCode) {
      setError('Pilih member tujuan invoice terlebih dahulu.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = { ...buildInvoicePayload(), recipient_member_code: memberInvoiceCode };
      const auditResponse = await channelApi.post('/admin/simulator/member-invoice/preview', payload, { timeout: 60000 });
      setMemberInvoiceAudit(auditResponse.data?.data || null);
      const pdfResponse = await channelApi.post('/admin/simulator/member-invoice/preview.pdf', payload, { responseType: 'blob', timeout: 60000 });
      const url = URL.createObjectURL(pdfResponse.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setEmailNotice(`Preview invoice untuk ${auditResponse.data?.data?.member?.memberName || memberInvoiceCode.toUpperCase()} dibuka di tab baru.`);
    } catch (e) {
      setError('Gagal preview invoice member: ' + apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const sendMemberInvoice = async () => {
    if (!memberInvoiceCode) {
      setError('Pilih member tujuan invoice terlebih dahulu.');
      return;
    }
    if (!form.email) {
      setError('Isi Customer Email — dipakai sebagai email tujuan member pada simulasi Internal Tools.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        ...buildInvoicePayload(),
        recipient_member_code: memberInvoiceCode,
        member_email: form.email,
      };
      const response = await channelApi.post('/admin/simulator/member-invoice/send', payload, { timeout: 60000 });
      setMemberInvoiceAudit((prev) => prev || {
        member: response.data?.data,
        formattedTotal: response.data?.data?.formattedTotal,
        components: [],
      });
      setEmailNotice(response.data?.message || `Invoice member dikirim ke ${form.email}`);
    } catch (e) {
      setError('Gagal mengirim invoice member: ' + apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const previewDummyInvoice = async () => {
    if (!form.code) {
      setError('Pilih produk atau package dari katalog sebelum preview invoice.');
      return;
    }
    setLoading(true);
    setError('');
    setEmailNotice('');
    try {
      const response = await channelApi.post('/admin/simulator/invoice/preview.html', buildInvoicePayload(), {
        responseType: 'blob',
        timeout: 60000,
      });
      const url = URL.createObjectURL(response.data);
      window.open(url, '_blank', 'noopener');
      setEmailNotice('Preview Dummy Invoice dibuka di tab baru (email-like HTML).');
    } catch (e) {
      setError('Gagal preview dummy invoice: ' + (e.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  };

  const sendDummyInvoice = async () => {
    if (!form.email) {
      setError('Isi Customer Email terlebih dahulu.');
      return;
    }
    if (!form.code) {
      setError('Pilih produk atau package dari katalog sebelum mengirim dummy invoice.');
      return;
    }
    setLoading(true);
    setError('');
    setEmailNotice('');
    try {
      const response = await channelApi.post('/admin/simulator/send-dummy-invoice', buildInvoicePayload(), { timeout: 60000 });
      setEmailNotice(response.data?.message || `Dummy invoice berhasil dikirim ke ${form.email}`);
    } catch (e) {
      setError('Gagal mengirim dummy invoice: ' + apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const sendDummyReservationEmail = async () => {
    if (!form.email) {
      setError('Isi Customer Email terlebih dahulu (hasil klik hotel dikirim kembali ke email ini).');
      return;
    }

    setLoading(true);
    setError('');
    setEmailNotice('');

    try {
      const hotelEmail = form.hotelEmail
        || selectedCatalogItem?.hotel_email
        || selectedCatalogItem?.HOTEL_EMAIL
        || selectedCatalogItem?.meta?.hotel_email
        || selectedCatalogItem?.meta?.hotelEmail
        || selectedCatalogItem?.member?.hotel_email
        || undefined;

      const response = await channelApi.post('/admin/simulator/test-reservation-email', {
        ...buildInvoicePayload(),
        email: form.email,
        name: form.name,
        travel_date: form.travelDate,
        ...stayDatePayload(selectedCatalogItem, form),
        display_name: form.displayName,
        room_name: form.displayName,
        hotel_name: selectedCatalogItem?.hotel_name
          || selectedCatalogItem?.meta?.hotel_name
          || selectedCatalogItem?.meta?.hotelName
          || selectedCatalogItem?.memberName
          || memberLabelFromCode(form.memberCode),
        hotel_code: selectedCatalogItem?.hotel_code
          || selectedCatalogItem?.meta?.hotel_code
          || selectedCatalogItem?.meta?.hotelCode
          || undefined,
        hotel_email: hotelEmail,
      }, { timeout: 60000 });

      setEmailNotice(response.data?.message || `Dummy reservasi dikirim ke hotel. Klik Tersedia/Full → hasil kembali ke ${form.email}`);
    } catch (e) {
      setError('Gagal mengirim dummy reservasi: ' + apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const preview = async (type) => {
    if (!result) return;
    setError('');

    try {
      if (!result.simulation && result.bookingId) {
        const response = await channelApi.get(`/admin/simulator/booking/${result.bookingId}/ticket.${type}`, {
          responseType: 'blob',
        });

        if (type === 'html') {
          window.open(URL.createObjectURL(response.data), '_blank', 'noopener');
        } else {
          saveBlob(response.data, `PRIME-${result.bookingId}.pdf`);
        }
        return;
      }

      // Re-resolve from the currently selected catalog item. Never reuse stale or
      // client-composed component arrays for a simulation preview.
      const response = await channelApi.post(`/admin/simulator/ticket-preview.${type}`, buildInvoicePayload(), {
        responseType: 'blob',
      });

      if (type === 'html') {
        window.open(URL.createObjectURL(response.data), '_blank', 'noopener');
      } else {
        saveBlob(response.data, `Indonesia-Journey-E-Ticket-${form.code}.pdf`);
      }
    } catch (e) {
      setError('Gagal membuat preview ticket: ' + (e.response?.data?.message || e.message));
    }
  };

  const previewHinHotelEmail = async (template, viewport = 'desktop') => {
    setError('');
    setEmailNotice('');

    try {
      if (!form.code) {
        setError('Pilih package catalog yang memiliki komponen hotel terlebih dahulu.');
        return;
      }
      const response = await channelApi.post('/admin/simulator/hin-hotel-email-preview.html', {
        ...buildInvoicePayload(),
        template,
      }, {
        responseType: 'blob',
      });
      const previewUrl = URL.createObjectURL(response.data);
      const dimensions = viewport === 'mobile' ? 'width=415,height=820' : 'width=920,height=900';
      window.open(previewUrl, '_blank', `noopener,${dimensions}`);
      setTimeout(() => URL.revokeObjectURL(previewUrl), 60000);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        const text = await e.response.data.text();
        try { e.response.data = JSON.parse(text); } catch { e.response.data = text; }
      }
      setError('Gagal membuka preview email HIN Hotel: ' + apiErrorMessage(e));
    }
  };

  const selectedCatalogItem = filteredCatalog.find((item) => itemKey(item) === selectedCatalogKey) || null;
  const hasHotel = itemHasHotel(selectedCatalogItem);
  const packageHasHotel = hasHotel && selectedCatalogItem?.type === 'package';
  const totalFiltered = filteredCatalog.length;

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[.18em] text-slate-500">Internal Integration Tools</div>
        <h1 className="mt-1 text-2xl font-bold text-slate-900">PRIME Purchase Simulator</h1>
        <p className="mt-1 text-sm text-slate-600">Simulasikan alur Channel → Purchase → Supplier → Credential → E-Ticket tanpa membuat marketplace baru.</p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1.1fr_.9fr]">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="font-bold">Purchase Request</h2>
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${form.dryRun ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
              {form.dryRun ? 'DRY RUN' : 'LIVE EXECUTE'}
            </span>
          </div>

          <div className="space-y-4">
            <div>
              <label className={label}>Type</label>
              <select
                className={field}
                value={form.kind}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    kind: e.target.value,
                    code: '',
                    displayName: '',
                    memberCode: '',
                  }));
                  setSelectedCatalogKey('');
                  setMemberInvoiceOptions([]);
                  setMemberInvoiceCode('');
                  setMemberInvoiceAudit(null);
                  setResult(null);
                }}
              >
                <option value="package">Package</option>
                <option value="product">Single Product</option>
              </select>
            </div>

            <CatalogCombobox
              groups={groupedCatalog}
              value={selectedCatalogKey}
              onChange={(value) => chooseCatalog({ target: { value } })}
              placeholder={`— Pilih ${form.kind === 'package' ? 'package' : 'single product'} —`}
              loading={initialLoading}
              typeLabel={form.kind === 'package' ? 'package' : 'single product'}
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className={label}>Code</label>
                <input className={field} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
              </div>
              <div>
                <label className={label}>Member</label>
                <input className={field} value={selectedCatalogItem?.member?.name || selectedCatalogItem?.memberName || memberLabelFromCode(form.memberCode)} readOnly />
              </div>
            </div>

            {selectedCatalogItem?.member?.code && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-center">
                  <div className="grid gap-1 text-slate-700 sm:grid-cols-3">
                    <div><span className="text-xs font-semibold uppercase text-slate-500">Member</span><br />{selectedCatalogItem.member.code}</div>
                    <div><span className="text-xs font-semibold uppercase text-slate-500">Integration</span><br />{selectedCatalogItem.integration?.status || 'Not available'}</div>
                    <div><span className="text-xs font-semibold uppercase text-slate-500">Transport</span><br />{selectedCatalogItem.integration?.transport || 'Not available'}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/internal-tools/api-documentation/${selectedCatalogItem.member.code}?tab=guide&product=${encodeURIComponent(itemCode(selectedCatalogItem))}`)}
                    className="inline-flex items-center justify-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    View Integration
                  </button>
                </div>
              </div>
            )}

            <div>
              <label className={label}>Display Name</label>
              <input className={field} value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
            </div>

            <div className={`grid gap-3 ${hasHotel ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
              <div>
                <label className={label}>{hasHotel ? 'Check-in' : 'Travel / Visit Date'}</label>
                <input
                  type="date"
                  className={field}
                  value={form.travelDate}
                  onChange={(e) => setForm({
                    ...form,
                    travelDate: e.target.value,
                    checkOutDate: packageHasHotel ? addCalendarDays(e.target.value, 1) : form.checkOutDate,
                  })}
                />
              </div>
              {hasHotel && (
                <div>
                  <label className={label}>Check-out {packageHasHotel ? '(H+1)' : ''}</label>
                  <input
                    type="date"
                    className={`${field} ${packageHasHotel ? 'bg-slate-50 text-slate-600' : ''}`}
                    min={addCalendarDays(form.travelDate, 1)}
                    value={packageHasHotel ? addCalendarDays(form.travelDate, 1) : form.checkOutDate}
                    readOnly={packageHasHotel}
                    onChange={(e) => setForm({ ...form, checkOutDate: e.target.value })}
                  />
                </div>
              )}
              <div>
                <label className={label}>Passenger</label>
                <input className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className={label}>Customer / Simulation Email</label>
                <input type="email" className={field} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="operator@example.com" />
                <p className="mt-1 text-[11px] text-slate-500">Hasil klik Kamar Tersedia/Full &amp; invoice member simulasi dikirim ke sini.</p>
              </div>
              <div>
                <label className={label}>Phone</label>
                <input className={field} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+62..." />
              </div>
            </div>

            {hasHotel && (
              <div>
                <label className={label}>Hotel Email Override (HIN)</label>
                <input
                  type="email"
                  className={field}
                  value={form.hotelEmail}
                  onChange={(e) => setForm({ ...form, hotelEmail: e.target.value })}
                  placeholder={selectedCatalogItem?.hotel_email || 'reservation@hotel.example'}
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Kosongkan untuk memakai Hotel Master / product meta
                  {selectedCatalogItem?.hotel_email ? ` (catalog: ${selectedCatalogItem.hotel_email})` : ''}.
                </p>
              </div>
            )}

            {!form.dryRun && (
              <div>
                <label className={label}>Execution Channel</label>
                <select className={field} value={form.channelClientId} onChange={(e) => setForm({ ...form, channelClientId: e.target.value })}>
                  <option value="">— Pilih registered channel —</option>
                  {channels.map((channel) => (
                    <option key={channel.client_id} value={channel.client_id}>
                      {channel.channel_name} {channel.env ? `(${channel.env})` : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-red-600">Live Execute menggunakan purchase flow asli: dapat mengurangi quota dan memanggil supplier API.</p>
              </div>
            )}

            <label className="flex cursor-pointer gap-3 rounded-xl bg-slate-50 p-3 text-sm">
              <input type="checkbox" checked={form.dryRun} onChange={(e) => setForm({ ...form, dryRun: e.target.checked })} />
              <span>
                <b>Dry Run (recommended)</b>
                <br />
                <span className="text-slate-500">Tidak menulis booking, tidak mengurangi quota, tidak call supplier, tidak kirim email.</span>
              </span>
            </label>

            <button
              disabled={loading || !form.code || !form.travelDate || !form.email || (!form.dryRun && !form.channelClientId)}
              onClick={execute}
              className="w-full rounded-xl bg-[#12284A] px-4 py-3 font-semibold text-white disabled:opacity-40"
            >
              {loading ? 'Processing…' : form.dryRun ? 'Run Simulation' : 'Execute Real Purchase Flow'}
            </button>

            <button
              disabled={loading || !form.email || !form.code}
              onClick={sendDummyEmail}
              className="w-full rounded-xl border border-[#12284A] bg-white px-4 py-3 font-semibold text-[#12284A] disabled:opacity-40"
            >
              {loading ? 'Mengirim…' : 'Kirim Dummy E-Ticket ke Email'}
            </button>

            <button
              disabled={loading || !form.email || !form.code}
              onClick={previewDummyInvoice}
              className="w-full rounded-xl border border-teal-800 bg-white px-4 py-3 font-semibold text-teal-900 disabled:opacity-40"
            >
              Preview Dummy Invoice
            </button>

            <button
              disabled={loading || !form.email || !form.code}
              onClick={sendDummyInvoice}
              className="w-full rounded-xl border border-teal-700 bg-teal-50 px-4 py-3 font-semibold text-teal-900 disabled:opacity-40"
            >
              {loading ? 'Mengirim…' : 'Kirim Dummy Invoice ke Email'}
            </button>

            <div className="rounded-xl border border-teal-200 bg-teal-50/50 p-4">
              <div className="font-semibold text-teal-950">Invoice Member (HIN / member lain)</div>
              <p className="mt-1 text-xs leading-5 text-teal-800">
                Berlaku untuk <b>single product</b> maupun <b>package</b>. Pada package, harga yang ditagihkan hanya produk milik member tujuan; produk member lain tetap tampil sebagai informasi.
              </p>
              <button
                type="button"
                disabled={loading || !form.code}
                onClick={loadMemberInvoiceOptions}
                className="mt-3 w-full rounded-lg border border-teal-700 bg-white px-3 py-2 text-sm font-semibold text-teal-900 disabled:opacity-40"
              >
                {loading ? 'Memuat…' : 'Cek Tujuan Invoice Member'}
              </button>
              {memberInvoiceOptions.length > 0 && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className={label}>Invoice diarahkan ke member</label>
                    <select className={field} value={memberInvoiceCode} onChange={(e) => { setMemberInvoiceCode(e.target.value); setMemberInvoiceAudit(null); }}>
                      {memberInvoiceOptions.map((member) => (
                        <option key={member.memberCode} value={member.memberCode}>
                          {member.memberName} ({member.memberCode.toUpperCase()}) · {member.componentCount} produk
                        </option>
                      ))}
                    </select>
                  </div>
                  <button type="button" disabled={loading || !memberInvoiceCode} onClick={previewMemberInvoice} className="w-full rounded-lg bg-teal-800 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40">
                    {loading ? 'Membuat Preview…' : 'Preview PDF Invoice Member'}
                  </button>
                  <button
                    type="button"
                    disabled={loading || !memberInvoiceCode || !form.email}
                    onClick={sendMemberInvoice}
                    className="w-full rounded-lg border border-teal-800 bg-white px-3 py-2 text-sm font-semibold text-teal-900 disabled:opacity-40"
                  >
                    {loading ? 'Mengirim…' : 'Kirim Invoice Member ke Email'}
                  </button>
                </div>
              )}
              {memberInvoiceAudit && (
                <div className="mt-4 rounded-lg border border-teal-200 bg-white p-3 text-xs">
                  <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2">
                    <span className="font-semibold text-slate-700">Harga terlihat untuk {memberInvoiceAudit.member?.memberName}</span>
                    <span className="font-bold text-teal-800">{memberInvoiceAudit.formattedTotal}</span>
                  </div>
                  <div className="mt-2 space-y-2">
                    {memberInvoiceAudit.components?.map((component, index) => (
                      <div key={`${component.name}-${index}`} className="flex items-start justify-between gap-3">
                        <span className="text-slate-700">{component.name}<span className="ml-1 text-slate-400">· {component.quantityLabel || `${component.quantity || 1} qty`}</span></span>
                        <span className={`shrink-0 font-semibold ${component.owned ? 'text-emerald-700' : 'text-slate-400'}`}>{component.owned ? component.formattedAmount : 'Info saja · tanpa harga'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              disabled={loading || !form.email || !form.code}
              onClick={sendDummyReservationEmail}
              className="w-full rounded-xl border border-emerald-700 bg-emerald-50 px-4 py-3 font-semibold text-emerald-800 disabled:opacity-40"
            >
              {loading ? 'Mengirim…' : 'Kirim Dummy Reservasi ke Hotel & Customer'}
            </button>

            <p className="text-xs text-slate-500">
              Flow HIN Internal Tools: kirim konfirmasi ke hotel dan informasi menunggu konfirmasi ke customer secara paralel → klik <b>Kamar Tersedia</b> / <b>Kamar Full</b> → hasil mengikuti konfigurasi email testing (tanpa menulis booking real).
              Untuk flow GOERS real, gunakan Live Execute pada kode <code className="rounded bg-slate-100 px-1">RSV-…</code>.
            </p>

            {emailNotice && <div className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">{emailNotice}</div>}
            {error && <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700 whitespace-pre-wrap">{error}</div>}
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="font-bold">Result & Ticket</h2>

          {!result ? (
            <div className="mt-5 rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">
              Hasil booking, credential, dan preview e-ticket akan muncul di sini.
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Booking ID</div>
                  <div className="mt-1 font-bold text-slate-900">{result.id || result.bookingId || '-'}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Invoice</div>
                  <div className="mt-1 font-bold text-slate-900">{result.invoiceCode || '-'}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Booking Time</div>
                  <div className="mt-1 font-bold text-slate-900">{formatDateTimeWib(result.createdAt || result.created_at || result.bookingTime || result.booking_time)}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Payment Time</div>
                  <div className="mt-1 font-bold text-slate-900">{formatDateTimeWib(result.paidAt || result.paid_at || result.paymentConfirmedAt || result.payment_confirmed_at)}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Status</div>
                  <div className="mt-1 font-bold text-slate-900">{result.paymentStatus || result.status || (form.dryRun ? 'SIMULATED' : 'Processed')}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Product Type</div>
                  <div className="mt-1 font-bold text-slate-900">{itemBadge({ type: form.kind })}</div>
                </div>
                {result.availabilitySimulation && (
                  <div className="rounded-xl bg-slate-50 p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Room Availability</div>
                    <div className="mt-1 font-bold text-slate-900">{result.availabilitySimulation}</div>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">Credentials</div>
                    <div className="text-xs text-slate-500">Preview credential hasil simulator atau purchase flow nyata.</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => preview('html')} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">Preview HTML</button>
                    <button onClick={() => preview('pdf')} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">Download PDF</button>
                  </div>
                </div>

                <div className="space-y-3">
                  {(result.vouchers || result.supplier_notification_status || []).length === 0 ? (
                    <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">Belum ada credential yang dikembalikan.</div>
                  ) : (
                    (result.vouchers || result.supplier_notification_status || []).map((voucher, index) => (
                      <div key={`${voucher.voucherCode || voucher.code || index}`} className="rounded-xl border border-slate-200 p-4">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700">{memberLabelFromCode(voucher.memberCode || form.memberCode)}</span>
                          <span className="text-xs text-slate-500">{voucher.status || 'Pending'}</span>
                        </div>
                        <div className="font-semibold text-slate-900">{voucher.componentName || voucher.name || form.displayName || '-'}</div>
                        <div className="mt-2 grid gap-2 text-sm text-slate-600">
                          <div><span className="font-semibold text-slate-700">Code:</span> {voucher.voucherCode || voucher.qrData || '-'}</div>
                          <div><span className="font-semibold text-slate-700">Supplier:</span> {voucher.supplierName || voucher.memberCode || 'InJourney Member'}</div>
                          <div><span className="font-semibold text-slate-700">Valid Until:</span> {voucher.validUntil || form.travelDate}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[.18em] text-slate-500">Hotel Email Preview</div>
            <h2 className="mt-1 font-bold text-slate-900">Preview dari Package Catalog Terpilih</h2>
            <p className="mt-1 text-sm text-slate-500">{form.code ? `${form.displayName} · ${form.code}` : 'Pilih package yang memiliki komponen hotel.'}</p>
          </div>
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">Admin INJ only</span>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          {hinHotelEmailTemplates.map((template) => (
            <div key={template.key} className="rounded-xl border border-slate-200 p-4">
              <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-[#12284A] text-white">
                <Mail className="h-4 w-4" />
              </div>
              <div className="font-semibold text-slate-900">{template.title}</div>
              <p className="mt-1 min-h-[40px] text-sm text-slate-500">{template.description}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" disabled={!form.code || loading} onClick={() => previewHinHotelEmail(template.key, 'desktop')} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                  <ExternalLink className="h-4 w-4" /> Desktop 800
                </button>
                <button type="button" disabled={!form.code || loading} onClick={() => previewHinHotelEmail(template.key, 'mobile')} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                  <ExternalLink className="h-4 w-4" /> Mobile 375
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
